import { useReportingStore } from "../../../../../stores/ReportingStore";
import LoaderStats from "../../LoaderStats";
import {
  ColorSwatch,
  Group,
  NumberFormatter,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { DonutChart } from "@mantine/charts";
import { useMemo, useState } from "react";
import CustomActiveShape from "../../reportingEmail/perf/CustomActiveShape";
import ReportingInfo from "../../ReportingInfo";
import { useLanguage } from "../../../../../stores/LanguageStore";

const LinkedinResponseQuality = () => {
  const lang = useLanguage((s) => s.language);
  const dateRange = useReportingStore((s) => s.dateRange);
  const actualStats = useReportingStore((s) => s.actualStats);
  const actualStatsDate = useReportingStore((s) => s.actualStatsDate);

  const [hoveredIndex, setHoveredIndex] = useState(0);

  const handleHover = (_, index) => {
    setHoveredIndex(index);
  };

  const statistics = useMemo(() => {
    if (!dateRange.start_date && !dateRange.end_date) {
      return actualStats;
    } else {
      const totals = actualStatsDate.reduce((acc, curr) => {
        Object.keys(curr).forEach((key) => {
          if (key !== "date") {
            acc[key] = (acc[key] || 0) + curr[key];
          }
        });
        return acc;
      }, {});

      return totals;
    }
  }, [actualStatsDate, dateRange.end_date, dateRange.start_date, actualStats]);

  const positiveRate = useMemo(() => {
    if (statistics.linkedin_message_answered) {
      return (
        (statistics.linkedin_message_answered_positive /
          statistics.linkedin_message_answered) *
        100
      );
    }
    return 0;
  }, [
    statistics.linkedin_message_answered,
    statistics.linkedin_message_answered_positive,
  ]);

  const negativeRate = useMemo(() => {
    if (statistics.linkedin_message_answered) {
      return (
        (statistics.linkedin_message_answered_negative /
          statistics.linkedin_message_answered) *
        100
      );
    }
    return 0;
  }, [
    statistics.linkedin_message_answered,
    statistics.linkedin_message_answered_negative,
  ]);

  const neutralRate = useMemo(() => {
    if (statistics.linkedin_message_answered) {
      return (
        (statistics.linkedin_message_answered_neutral /
          statistics.linkedin_message_answered) *
        100
      );
    }
    return 0;
  }, [
    statistics.linkedin_message_answered,
    statistics.linkedin_message_answered_neutral,
  ]);

  const data = useMemo(
    () => [
      {
        name: lang.reports.others,
        value: Math.round(neutralRate * 10) / 10,
        full: statistics.linkedin_message_answered_neutral,
        color: "gray.5",
      },
      {
        name: lang.statistics.contacts_type.postitive,
        value: Math.round(positiveRate * 10) / 10,
        full: statistics.linkedin_message_answered_positive,
        color: "green",
      },
      {
        name: lang.statistics.contacts_type.negative,
        value: Math.round(negativeRate * 10) / 10,
        full: statistics.linkedin_message_answered_negative,
        color: "red",
      },
    ],
    [
      negativeRate,
      neutralRate,
      positiveRate,
      statistics.linkedin_message_answered_negative,
      statistics.linkedin_message_answered_neutral,
      statistics.linkedin_message_answered_positive,
      lang,
    ]
  );

  return (
    <Stack pos={"relative"} flex={1} align="center">
      <LoaderStats isDate />

      <Group mb={16} justify="center" w={"100%"} px={"md"}>
        <ReportingInfo>
          <Text>{lang.reports.answers_title}</Text>
        </ReportingInfo>

        <Text size="lg" fw={600}>
          {lang.reports.answers_quality}{" "}
          <span style={{ color: "black" }}>
            ({" "}
            <b>
              <NumberFormatter value={statistics.linkedin_message_answered} />
            </b>{" "}
            )
          </span>
        </Text>
      </Group>

      <Group>
        {statistics.linkedin_message_answered > 0 ? (
          <DonutChart
            w={"1100%"}
            h={250}
            data={data}
            thickness={32}
            withTooltip={false}
            paddingAngle={3}
            pieProps={{
              isAnimationActive: true,
              animationDuration: 600,
              activeShape: (props) => <CustomActiveShape {...props} />,
              activeIndex: hoveredIndex,
              onMouseEnter: handleHover,
            }}
          />
        ) : (
          <Text w={"100%"} ta={"center"} h={100} mt={64}>{lang.reports.no_data}</Text>
        )}

        <Group gap={"md"} justify="center" w={"100%"}>
          <Group gap={4}>
            <ColorSwatch color={`var(--mantine-color-gray-5)`} size={16} />
            <Text size="sm">{lang.reports.others}</Text>
          </Group>
          <Group gap={4}>
            <ColorSwatch color={`var(--mantine-color-green-5)`} size={16} />
            <Text size="sm">{lang.statistics.contacts_type.postitive}</Text>
          </Group>
          <Group gap={4}>
            <ColorSwatch color={`var(--mantine-color-red-5)`} size={16} />
            <Text size="sm">{lang.statistics.contacts_type.negative}</Text>
          </Group>
        </Group>
      </Group>
    </Stack>
  );
};

export default LinkedinResponseQuality;
